import { RELEASE_NOTES_PATH, parseReleaseNotes, type ReleaseNotes } from '@shared/release-notes';

/** Build id embedded in the JS bundle at compile time. */
export const APP_BUILD_ID =
  typeof __APP_BUILD_ID__ !== 'undefined' ? __APP_BUILD_ID__ : 'dev';

/** When the commit this app was built from reached main (ISO); null in dev and under Jest. */
export const APP_BUILT_FROM_AT: string | null =
  typeof __APP_BUILT_FROM_AT__ !== 'undefined' ? __APP_BUILT_FROM_AT__ : null;

export type VersionInfo = {
  buildId: string;
};

const VERSION_URL = '/version.json';

/** Poll interval while the tab/app is visible (ms). */
export const APP_UPDATE_POLL_MS = 5 * 60 * 1000;

/**
 * Fetches the deployed build id from /version.json (always network, never cache).
 * Returns null if the request fails (e.g. local Vite without the asset).
 */
export async function fetchRemoteBuildId(): Promise<string | null> {
  try {
    const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    const data = (await response.json()) as Partial<VersionInfo>;
    const buildId = typeof data.buildId === 'string' ? data.buildId.trim() : '';
    return buildId || null;
  } catch {
    return null;
  }
}

/**
 * What the deployed build says changed (/changes.json, T-16), always from the network. Null if it
 * can't be read: a local dev server, a build with no git, or a failed request.
 */
export async function fetchReleaseNotes(): Promise<ReleaseNotes | null> {
  try {
    const response = await fetch(`${RELEASE_NOTES_PATH}?t=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    return parseReleaseNotes(await response.json());
  } catch {
    return null;
  }
}

/** True when the server is serving a different build than this running app. */
export function isNewerBuild(remoteBuildId: string | null): boolean {
  return Boolean(remoteBuildId && remoteBuildId !== APP_BUILD_ID);
}

/** One-shot query param so forced reloads bypass iOS Safari soft-reload/cache. */
export const APP_RELOAD_PARAM = '__app_reload';

let appReloading = false;

/** True once reloadApp has started a navigation in this page (lazyPage waits for it). */
export function isAppReloading(): boolean {
  return appReloading;
}

/**
 * Force a full document navigation to the current URL.
 * Prefer this over `location.reload()` — on iOS Safari (and home-screen installs)
 * reload() can soft-reload into a blank white screen until a manual browser refresh.
 */
export function reloadApp(): void {
  appReloading = true;
  const url = new URL(window.location.href);
  url.searchParams.set(APP_RELOAD_PARAM, String(Date.now()));
  window.location.replace(url.toString());
}

/** Strip the cache-bust param after a forced reload so the address bar stays clean. */
export function cleanReloadParamFromUrl(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(APP_RELOAD_PARAM)) return;
  url.searchParams.delete(APP_RELOAD_PARAM);
  const cleaned = `${url.pathname}${url.search}${url.hash}` || '/';
  window.history.replaceState(window.history.state, '', cleaned);
}
